import { Component, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastService } from '../../ui';
import { CustomerInformationService } from './customer-information.service';
import {
  HLM_DIALOG_SHELL,
  HlmBtnDirective,
  HlmIconComponent,
  HlmSpinnerComponent,
} from '../../ui';

@Component({
  selector: 'cmt-delete-customer-dialog',
  standalone: true,
  imports: [...HLM_DIALOG_SHELL, HlmBtnDirective, HlmIconComponent, HlmSpinnerComponent],
  template: `
    <hlm-dialog-surface size="sm">
      <hlm-dialog-header>
        <hlm-dialog-icon-badge tone="destructive">
          <hlm-icon name="lucideTriangleAlert" size="1.1rem" />
        </hlm-dialog-icon-badge>
        <div hlmDialogHeading>
          <h2 hlmDialogTitle>Delete Customer Data</h2>
          <p hlmDialogDescription>
            This permanently removes the customer record. This action cannot be undone.
          </p>
        </div>
      </hlm-dialog-header>

      <section hlmDialogBody>
        <div class="rounded-lg border border-destructive/25 bg-destructive/5 p-3 text-sm text-foreground">
          <p class="m-0">
            <strong class="text-destructive">Attention!</strong>
            Are you sure you want to delete this customer's data?
          </p>
        </div>
      </section>

      <footer hlmDialogFooter>
        <button hlmBtn variant="outline" (click)="dialogRef.close()" [disabled]="deleting()">
          Cancel
        </button>
        <button hlmBtn variant="destructive" (click)="confirmDelete()" [disabled]="deleting()">
          @if (deleting()) {
            <hlm-spinner size="16" />
            Deleting…
          } @else {
            <hlm-icon name="lucideTrash2" size="0.9rem" />
            Delete
          }
        </button>
      </footer>
    </hlm-dialog-surface>
  `,
})
export class DeleteCustomerDialogComponent {
  private readonly data = inject<{ customerId: number }>(MAT_DIALOG_DATA);
  readonly dialogRef = inject(MatDialogRef<DeleteCustomerDialogComponent>);
  private readonly service = inject(CustomerInformationService);
  private readonly toast = inject(ToastService);

  readonly deleting = signal(false);

  confirmDelete(): void {
    this.deleting.set(true);
    this.service.deleteCustomerData(this.data.customerId).subscribe({
      next: () => {
        this.toast.open('Customer data successfully deleted', 'OK', { duration: 4000 });
        this.dialogRef.close(true);
      },
      error: () => {
        this.toast.open('Data deletion failed. Contact support.', 'OK', { duration: 6000 });
        this.deleting.set(false);
      },
    });
  }
}
